import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Cross, Shield } from 'lucide-react';
import { adminAuthService } from '../Services/adminAuthService';
import { useTheme } from '../contexts/ThemeContext';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { FREE_DAILY_SEARCHES } from '../config/quotas';
import { threadFollowService } from '../Services/threadFollowService';
import { useMessage } from '../contexts/MessageContext';

// Imports lucide-react (organisés et sans doublons)
import {
  Heart,
  MessageSquare,
  Pill,
  Stethoscope,
  Home as HomeIcon,
  Users,
  AlertCircle,
  History,
  User,
  Settings,
  LogOut,
  Info,
  Menu,
  X,
  Moon,
  Sun,
  Bell,
  HeartPulse,
  Activity,
  Sparkles,
  Mail,
  Crown,
  Droplet,
  HelpCircle,
  AlertTriangle,
} from 'lucide-react';

import { Button } from './ui/button';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

const DashboardLayout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const { user, logout, hasPremiumAccess, isTrial, trialCountdownLabel } = useAuth();
  const { unreadCount: unreadMessages } = useMessage();
  const [userProfile, setUserProfile] = useState(null);

  useEffect(() => {
    const fetchProfile = async () => {
      if (!user?.uid) return;

      try {
        const docRef = doc(db, 'users', user.uid);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          setUserProfile(docSnap.data());
        }
      } catch (err) {
        console.error('Erreur chargement profil:', err);
      }
    };

    fetchProfile();
  }, [user]);

  const [sidebarOpen, setSidebarOpen] = useState(false);

  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    if (!user?.uid) return;
    const loadCount = async () => {
      const count = await threadFollowService.getUnreadCount(user.uid);
      setUnreadCount(count);
    };
    loadCount();
    const interval = setInterval(loadCount, 30000);
    return () => clearInterval(interval);
  }, [user]);

  const renderAvatarIcon = (type) => {
    switch (type) {
      case 'medical':
        return <Stethoscope className="w-5 h-5 text-white" />;
      case 'nurse':
        return <HeartPulse className="w-5 h-5 text-white" />;
      case 'helper':
        return <Users className="w-5 h-5 text-white" />;
      case 'doctor':
        return <Activity className="w-5 h-5 text-white" />;
      case 'modern':
        return <Sparkles className="w-5 h-5 text-white" />;
      default:
        return <User className="w-5 h-5 text-white" />;
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      toast.success('Déconnexion réussie');
      navigate('/auth');
    } catch (error) {
      toast.error('Erreur lors de la déconnexion');
    }
  };

  // ═══════════════════════════════════════════════════════════
  // 🆕 NAVIGATION RÉORGANISÉE PAR CATÉGORIES
  // ═══════════════════════════════════════════════════════════

  // 🤖 IA & Santé
  const aiNavigation = [
    { name: 'Chat IA', path: '/dashboard', icon: MessageSquare },
    { name: 'Médicaments', path: '/medications', icon: Pill },
    { name: 'Analyse de Sang', path: '/blood-test-analysis', icon: Droplet },
    { name: 'Ma Pharmacie', path: '/pharmacy', icon: Cross },
    { name: 'Pilulier', path: '/pilulier', icon: Bell, badge: 'Bientôt' },
    { name: 'Mon Carnet de Santé', path: '/health-record', icon: HeartPulse},
  ];

  // 📚 Communauté
  const communityNavigation = [
    { name: 'Articles', path: '/daily-help', icon: HomeIcon },
    { name: 'Forum Communautaire', path: '/forum', icon: Users },
  ];

  // ⚙️ Outils
  const toolsNavigation = [
    { name: 'Alertes', path: '/alertes-sante', icon: AlertTriangle, badgeInline: 'Air & Pollens' },
    { name: 'Ressource Utile', path: '/emergency', icon: AlertCircle },
    { name: 'Historique', path: '/history', icon: History },
    { name: 'FAQ', path: '/faq', icon: HelpCircle },
  ];

  // 👑 Premium
  const premiumNavigation = [{ name: 'Premium', path: '/pricing', icon: Crown }];

  // 💬 Aide
  const helpNavigation = [{ name: 'Feedback', path: '/feedback', icon: Mail }];

  // Fonction pour rendre un lien de navigation
  const renderNavLink = (item, options = {}) => {
    const isActive = location.pathname === item.path;
    const isPremiumLink = item.name === 'Premium';
    const isFeedbackLink = item.name === 'Feedback';

    // Style spécial pour Feedback
    if (isFeedbackLink) {
      return (
        <Link
          key={item.path}
          to={item.path}
          onClick={() => setSidebarOpen(false)}
          data-testid={`nav-link-${item.path.substring(1)}`}
          className={`flex items-center gap-3 px-4 py-3 rounded-xl font-medium transition-all bg-gradient-to-r from-red-500 via-orange-500 to-red-500 text-white hover:shadow-lg ${
            isActive ? 'shadow-lg ring-2 ring-red-300' : ''
          }`}
        >
          <item.icon className="w-5 h-5" />
          <span>{item.name}</span>
        </Link>
      );
    }

    // Style spécial pour Premium
    if (isPremiumLink && !isActive) {
      return (
        <Link
          key={item.path}
          to={item.path}
          onClick={() => setSidebarOpen(false)}
          data-testid={`nav-link-${item.path.substring(1)}`}
          className="flex items-center gap-3 px-4 py-3 rounded-xl font-medium transition-all bg-gradient-to-r from-amber-500/20 to-orange-500/20 dark:from-amber-500/30 dark:to-orange-500/30 text-amber-700 dark:text-amber-300 border border-amber-500/30 hover:from-amber-500/30 hover:to-orange-500/30 shadow-lg shadow-amber-500/10"
        >
          <item.icon className="w-5 h-5 animate-pulse" />
          <span>{item.name}</span>
          {/* Pastille d'upsell : sur l'ACCÈS, pas sur l'abonnement — inutile pendant l'essai. */}
          {!hasPremiumAccess && (
            <span className="ml-auto text-xs bg-amber-500 text-white px-2 py-0.5 rounded-full">
              Upgrade
            </span>
          )}
        </Link>
      );
    }

    // Style standard
    return (
      <Link
        key={item.path}
        to={item.path}
        onClick={() => setSidebarOpen(false)}
        data-testid={`nav-link-${item.path.substring(1)}`}
        className={`
      flex items-center gap-3 px-4 py-3 rounded-xl font-medium transition-all
      ${
        isActive
          ? 'bg-primary dark:bg-blue-600 text-white shadow-md shadow-primary/20 dark:shadow-blue-500/20'
          : 'text-slate-700 dark:text-gray-300 hover:bg-slate-100 dark:hover:bg-gray-800'
      }
    `}
      >
        <item.icon className="w-5 h-5" />
        <span>{item.name}</span>
        {item.badgeInline && (
          <span className="ml-1 px-1.5 py-0.5 bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-300 text-[10px] font-bold rounded-full">
            {item.badgeInline}
          </span>
        )}
        {item.badge && (
          <span className="ml-auto px-2 py-0.5 text-xs font-medium bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-full">
            {item.badge}
          </span>
        )}
      </Link>
    );
  };

  // Composant Divider
  const Divider = () => (
    <div
      style={{ marginTop: '10px', marginBottom: '10px' }}
      className="border-t border-slate-200 dark:border-gray-800"
    ></div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-gray-950 dark:to-gray-900 transition-colors duration-300">
      {/* Top Navbar */}
      <header className="sticky top-0 z-50 bg-white/90 dark:bg-gray-900/90 backdrop-blur-md border-b border-slate-200 dark:border-gray-800 shadow-sm transition-colors duration-300">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <div className="flex items-center gap-3">
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="lg:hidden p-2 hover:bg-slate-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                data-testid="mobile-menu-button"
              >
                {sidebarOpen ? (
                  <X className="w-5 h-5 text-gray-900 dark:text-gray-100" />
                ) : (
                  <Menu className="w-5 h-5 text-gray-900 dark:text-gray-100" />
                )}
              </button>
              <div className="flex items-center gap-2">
                <img src="/RemaH.png" alt="Remadia Logo" className="w-24 h-24 object-contain" />
                <span className="text-xl font-heading font-bold text-foreground dark:text-gray-100"></span>
              </div>
            </div>

            {/* Right side */}
            <div className="flex items-center gap-4">
              {/* Dark Mode Toggle */}
              <Button
                onClick={toggleTheme}
                variant="outline"
                size="sm"
                className="rounded-full w-10 h-10 p-0 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700 transition-all duration-300 shadow-md"
                data-testid="theme-toggle-button"
              >
                {theme === 'dark' ? (
                  <Sun className="w-5 h-5 text-yellow-500" />
                ) : (
                  <Moon className="w-5 h-5 text-gray-700" />
                )}
              </Button>

              {/* Subscription Badge — 3 états : essai · gratuit · abonné.
                  Le compteur N/3 n'est affiché QUE sans accès premium : pendant
                  l'essai le serveur ne décompte plus le quota (chatService.js),
                  l'afficher serait faux. */}
              {/* Bleu `primary` (#0EA5E9) : distinct de l'ambre du badge Gratuit
                  juste en dessous et du vert du badge Premium juste après. */}
              {isTrial && (
                <div
                  className="hidden sm:flex items-center gap-2 px-3 py-1.5 bg-primary/10 dark:bg-sky-900/30 text-primary dark:text-sky-300 rounded-full text-sm font-medium border border-primary/30 dark:border-sky-800"
                  data-testid="trial-badge"
                >
                  <Crown className="w-4 h-4" />
                  <span>Essai — {trialCountdownLabel}</span>
                </div>
              )}

              {!hasPremiumAccess && (
                <div className="hidden sm:flex items-center gap-3">
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300 rounded-full text-sm font-medium border border-amber-200 dark:border-amber-800">
                    <span>Gratuit</span>
                    <span className="text-xs font-bold px-1.5 py-0.5 bg-amber-200 dark:bg-amber-800 rounded">
                      {Math.max(0, FREE_DAILY_SEARCHES - (user?.daily_search_count || 0))}/{FREE_DAILY_SEARCHES}
                    </span>
                  </div>

                  {/* Bouton Upgrade si quota épuisé */}
                  {user?.daily_search_count >= FREE_DAILY_SEARCHES && (
                    <button
                      onClick={() => navigate('/pricing')}
                      className="px-4 py-1.5 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-full text-sm font-medium hover:scale-105 transition-transform"
                    >
                      ⚡ Passer Premium
                    </button>
                  )}
                </div>
              )}

              {user?.subscription_type === 'premium' && (
                <div
                  className="hidden sm:flex items-center gap-2 px-3 py-1.5 bg-accent/10 dark:bg-green-900/30 text-accent dark:text-green-300 rounded-full text-sm font-medium border border-green-200 dark:border-green-800"
                  data-testid="subscription-badge"
                >
                  Premium
                </div>
              )}

              {/* Badge Messages système */}
              <button
                onClick={() => navigate('/messages')}
                className="relative p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-gray-800 transition-colors"
                aria-label={`Messages${unreadMessages > 0 ? ` (${unreadMessages} non lus)` : ''}`}
                title="Messages"
              >
                <Mail className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                {unreadMessages > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                    {unreadMessages > 9 ? '9+' : unreadMessages}
                  </span>
                )}
              </button>

              {/* User Menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className="relative flex items-center gap-2 hover:opacity-80 transition-opacity"
                    data-testid="user-menu-button"
                  >
                    {unreadCount > 0 && (
                      <span className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full border-2 border-white dark:border-gray-900 z-10" />
                    )}
                    <Avatar className="w-10 h-10 border-2 border-primary/20 dark:border-blue-500/30">
                      <AvatarImage src={user?.profile_picture} />
                      <AvatarFallback className="bg-primary dark:bg-blue-600 flex items-center justify-center">
                        {renderAvatarIcon(userProfile?.avatar?.type)}
                      </AvatarFallback>
                    </Avatar>
                  </button>
                </DropdownMenuTrigger>

                <DropdownMenuContent
                  align="end"
                  className="w-56 bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700"
                >
                  <div className="px-2 py-1.5">
                    <p className="text-sm font-medium text-foreground dark:text-gray-100">
                      {user?.first_name} {user?.last_name}
                    </p>
                    <p className="text-xs text-muted dark:text-gray-400">{user?.email}</p>
                  </div>

                  <DropdownMenuSeparator className="bg-gray-200 dark:bg-gray-700" />

                  <DropdownMenuItem
                    onClick={() => navigate('/about')}
                    data-testid="about-menu-item"
                    className="dark:hover:bg-gray-700 dark:text-gray-200"
                  >
                    <Info className="w-4 h-4 mr-2" />
                    Qui sommes-nous
                  </DropdownMenuItem>

                  <DropdownMenuItem
                    onClick={() => navigate('/profile')}
                    data-testid="profile-menu-item"
                    className="dark:hover:bg-gray-700 dark:text-gray-200"
                  >
                    <User className="w-4 h-4 mr-2" />
                    Profil
                    {unreadCount > 0 && (
                      <span className="ml-auto w-2 h-2 bg-green-500 rounded-full" />
                    )}
                  </DropdownMenuItem>

                  <DropdownMenuItem
                    onClick={() => navigate('/settings')}
                    data-testid="settings-menu-item"
                    className="dark:hover:bg-gray-700 dark:text-gray-200"
                  >
                    <Settings className="w-4 h-4 mr-2" />
                    Paramètres
                  </DropdownMenuItem>

                  <DropdownMenuSeparator className="bg-gray-200 dark:bg-gray-700" />

                  {/* Visible pour tous */}
                  <DropdownMenuItem
                    onClick={() => navigate('/whats-new')}
                    data-testid="whats-new-menu-item"
                    className="dark:hover:bg-gray-700 dark:text-gray-200"
                  >
                    <Sparkles className="w-4 h-4 mr-2" />
                    Nouveautés
                  </DropdownMenuItem>

                  {/* Admin uniquement */}
                  {adminAuthService.isAdmin(user) && (
                    <>
                      <DropdownMenuItem
                        onClick={() => navigate('/admin/dashboard')}
                        className="dark:hover:bg-gray-700 dark:text-gray-200"
                      >
                        <Shield className="w-4 h-4 mr-2" />
                        Dashboard Admin
                      </DropdownMenuItem>
                      <DropdownMenuSeparator className="bg-gray-200 dark:bg-gray-700" />
                    </>
                  )}

                  <DropdownMenuItem
                    onClick={handleLogout}
                    className="text-destructive dark:text-red-400 dark:hover:bg-gray-700"
                    data-testid="logout-menu-item"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Déconnexion
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <aside
          className={`
            fixed lg:sticky top-[73px] left-0 z-40 h-[calc(100vh-73px)]
            w-64 bg-white dark:bg-gray-900 border-r border-slate-200 dark:border-gray-800 transition-all duration-300
            ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          `}
          data-testid="sidebar"
        >
          <nav className="p-4 space-y-1 overflow-y-auto h-full">
            {/* ═══════════════════════════════════════════════════════════ */}
            {/* 🤖 IA & Santé */}
            {/* ═══════════════════════════════════════════════════════════ */}
            <div className="space-y-1">{aiNavigation.map((item) => renderNavLink(item))}</div>

            <Divider />

            {/* ═══════════════════════════════════════════════════════════ */}
            {/* 📚 Communauté */}
            {/* ═══════════════════════════════════════════════════════════ */}
            <div className="space-y-1">
              {communityNavigation.map((item) => renderNavLink(item))}
            </div>

            <Divider />

            {/* ═══════════════════════════════════════════════════════════ */}
            {/* ⚙️ Outils */}
            {/* ═══════════════════════════════════════════════════════════ */}
            <div className="space-y-1">{toolsNavigation.map((item) => renderNavLink(item))}</div>

            <Divider />

            {/* ═══════════════════════════════════════════════════════════ */}
            {/* 👑 Premium */}
            {/* ═══════════════════════════════════════════════════════════ */}
            <div className="space-y-1">{premiumNavigation.map((item) => renderNavLink(item))}</div>

            <Divider />

            {/* ═══════════════════════════════════════════════════════════ */}
            {/* 🌙 Mode Sombre */}
            {/* ═══════════════════════════════════════════════════════════ */}
            <div className="p-3 bg-gradient-to-br from-blue-50 to-purple-50 dark:from-blue-950/30 dark:to-purple-950/30 rounded-lg border border-blue-200 dark:border-blue-900">
              <div className="flex items-center gap-2 mb-1">
                {theme === 'dark' ? (
                  <Moon className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                ) : (
                  <Sun className="w-4 h-4 text-amber-600" />
                )}
                <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                  Mode {theme === 'dark' ? 'Sombre' : 'Clair'}
                </span>
              </div>
              <p className="text-xs text-gray-600 dark:text-gray-400">
                Protégez vos yeux avec le thème adapté
              </p>
            </div>

            <Divider />

            {/* ═══════════════════════════════════════════════════════════ */}
            {/* 💬 Aide (FAQ + Feedback) */}
            {/* ═══════════════════════════════════════════════════════════ */}
            <div className="space-y-2">{helpNavigation.map((item) => renderNavLink(item))}</div>
          </nav>
        </aside>

        {/* Overlay for mobile */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 bg-black/50 dark:bg-black/70 z-30 lg:hidden backdrop-blur-sm"
            onClick={() => setSidebarOpen(false)}
          ></div>
        )}

        {/* Main Content */}
        <main className="flex-1 flex justify-center p-6 lg:p-8">
          <div className="w-full max-w-5xl">{children}</div>
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;
